import clsx from "clsx";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDashed,
  Inbox,
  Loader2,
  OctagonAlert,
  RefreshCw,
  TriangleAlert,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";

import { errorMessage } from "@/lib/api";
import type { Level } from "@/lib/health";

// --- status -------------------------------------------------------------------------------

const LEVEL_STYLE: Record<Level, { color: string; icon: LucideIcon }> = {
  good: { color: "var(--color-good)", icon: CheckCircle2 },
  warning: { color: "var(--color-warning)", icon: TriangleAlert },
  serious: { color: "var(--color-serious)", icon: AlertTriangle },
  critical: { color: "var(--color-critical)", icon: OctagonAlert },
  neutral: { color: "var(--color-muted)", icon: CircleDashed },
};

export function levelColor(level: Level): string {
  return LEVEL_STYLE[level].color;
}

/**
 * A status: an icon in the status colour and a label in ordinary ink. The icon's shape
 * differs per level, so the meaning survives without the colour.
 */
export function StatusBadge({
  level,
  label,
  title,
  className,
}: {
  level: Level;
  label: string;
  title?: string;
  className?: string;
}) {
  const { color, icon: Icon } = LEVEL_STYLE[level];
  return (
    <span className={clsx("inline-flex items-center gap-1.5 whitespace-nowrap", className)} title={title}>
      <Icon size={15} style={{ color }} aria-hidden strokeWidth={2.25} />
      <span className="text-ink-2">{label}</span>
    </span>
  );
}

/**
 * A ratio against a limit. The fill carries the severity; the track is the same colour,
 * faint, so the state reads across the whole bar.
 */
export function Meter({
  value,
  level,
  label,
  className,
}: {
  /** 0 to 100, or null if unknown. */
  value: number | null;
  level: Level;
  /** For screen readers, e.g. "Battery". */
  label: string;
  className?: string;
}) {
  const color = level === "neutral" ? "var(--color-series)" : levelColor(level);
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value ?? undefined}
      aria-valuetext={value === null ? "unknown" : `${Math.round(value)}%`}
      className={clsx("h-1.5 overflow-hidden rounded-full", className)}
      style={{ background: `color-mix(in srgb, ${color} 22%, transparent)` }}
    >
      <div
        className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${value ?? 0}%`, background: color }}
      />
    </div>
  );
}

// --- buttons ------------------------------------------------------------------------------

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

const BUTTON_STYLE: Record<ButtonVariant, string> = {
  primary: "bg-accent text-white hover:bg-accent-hover",
  secondary: "border border-axis bg-raised text-ink hover:border-muted",
  ghost: "text-ink-2 hover:bg-white/5 hover:text-ink",
  danger: "bg-critical text-white hover:brightness-110",
};

export function Button({
  variant = "secondary",
  size = "md",
  busy = false,
  icon: Icon,
  children,
  className,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: "sm" | "md";
  /** Shows a spinner and blocks clicks while something is in progress. */
  busy?: boolean;
  icon?: LucideIcon;
}) {
  const Shown = busy ? Loader2 : Icon;
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={clsx(
        "inline-flex items-center justify-center gap-1.5 rounded-[7px] font-medium transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-45",
        size === "sm" ? "px-2.5 py-1 text-[13px]" : "px-3.5 py-[7px]",
        BUTTON_STYLE[variant],
        className,
      )}
    >
      {Shown && <Shown size={size === "sm" ? 14 : 16} className={busy ? "animate-spin" : undefined} aria-hidden />}
      {children}
    </button>
  );
}

// --- states -------------------------------------------------------------------------------

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 p-10 text-muted">
      <Loader2 size={18} className="animate-spin" aria-hidden />
      <span>{label}…</span>
    </div>
  );
}

export function EmptyState({
  title,
  children,
  icon: Icon = Inbox,
  action,
}: {
  title: string;
  children?: ReactNode;
  icon?: LucideIcon;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <Icon size={28} className="text-muted" aria-hidden strokeWidth={1.5} />
      <p className="font-medium text-ink">{title}</p>
      {children && <p className="max-w-md text-ink-2">{children}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <OctagonAlert size={28} style={{ color: "var(--color-critical)" }} aria-hidden strokeWidth={1.5} />
      <p className="font-medium text-ink">This could not be loaded</p>
      <p className="max-w-md text-ink-2">{errorMessage(error)}</p>
      {onRetry && (
        <Button icon={RefreshCw} onClick={onRetry} className="mt-2">
          Try again
        </Button>
      )}
    </div>
  );
}

/** An error inside a form or a dialog. */
export function InlineError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" className="flex items-start gap-1.5 text-[13px] text-ink">
      <OctagonAlert size={15} className="mt-0.5 shrink-0" style={{ color: "var(--color-critical)" }} aria-hidden />
      {errorMessage(error)}
    </p>
  );
}

/**
 * Wraps content that is fetched. While new data loads the previous content stays,
 * dimmed, so the page doesn't jump.
 */
export function Loadable({
  query,
  children,
  empty,
}: {
  query: { isPending: boolean; isError: boolean; error: unknown; isPlaceholderData?: boolean; refetch(): unknown };
  children: ReactNode;
  /** Shown instead of the children when there is nothing to show. */
  empty?: ReactNode | false;
}) {
  if (query.isPending) return <Spinner />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (empty) return <>{empty}</>;
  return (
    <div className={clsx("transition-opacity", query.isPlaceholderData && "opacity-50")}>{children}</div>
  );
}

// --- layout pieces ------------------------------------------------------------------------

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="truncate text-xl font-semibold">{title}</h1>
        {subtitle && <div className="mt-0.5 text-ink-2">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function Section({
  title,
  actions,
  children,
  className,
  padded = true,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section className={clsx("card", className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
          <h2 className="font-semibold">{title}</h2>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={padded ? "p-4" : undefined}>{children}</div>
    </section>
  );
}

/** One headline number. The number is the chart. */
export function StatTile({
  label,
  value,
  detail,
  level,
  onClick,
  active,
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  /** Adds a status icon next to the label. */
  level?: Level;
  onClick?: () => void;
  /** Marks the tile as the current filter. */
  active?: boolean;
}) {
  const Icon = level ? LEVEL_STYLE[level].icon : null;
  const body = (
    <>
      <div className="flex items-center gap-1.5 text-[13px] text-ink-2">
        {Icon && level && <Icon size={14} style={{ color: levelColor(level) }} aria-hidden strokeWidth={2.25} />}
        {label}
      </div>
      <div className="mt-1 text-[28px] font-semibold leading-tight">{value}</div>
      {detail && <div className="mt-0.5 text-[13px] text-muted">{detail}</div>}
    </>
  );
  const classes = clsx(
    "card flex h-full w-full flex-col items-start justify-start px-4 py-3 text-left",
    active && "ring-2 ring-link",
  );
  return onClick ? (
    <button type="button" onClick={onClick} aria-pressed={active} className={clsx(classes, "hover:bg-raised")}>
      {body}
    </button>
  ) : (
    <div className={classes}>{body}</div>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange(page: number): void;
}) {
  if (total <= pageSize) return null;
  const last = Math.max(0, Math.ceil(total / pageSize) - 1);
  return (
    <nav className="flex items-center justify-end gap-2 border-t border-line px-4 py-2.5" aria-label="Pages">
      <span className="numeric mr-2 text-[13px] text-muted">
        {page * pageSize + 1}–{Math.min(total, (page + 1) * pageSize)} of {total}
      </span>
      <Button size="sm" icon={ChevronLeft} disabled={page === 0} onClick={() => onChange(page - 1)}>
        Previous
      </Button>
      <Button size="sm" disabled={page >= last} onClick={() => onChange(page + 1)}>
        Next
        <ChevronRight size={14} aria-hidden />
      </Button>
    </nav>
  );
}

// --- dialog -------------------------------------------------------------------------------

/** A modal dialog on the browser's own <dialog>: focus trap and Escape come with it. */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  wide = false,
}: {
  open: boolean;
  onClose(): void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  if (!open) return null;
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose(); // a click on the backdrop
      }}
      className={clsx(
        "m-auto w-[calc(100vw-2rem)] rounded-xl border border-white/10 bg-surface p-0 text-ink",
        "shadow-2xl backdrop:bg-black/60",
        wide ? "max-w-3xl" : "max-w-lg",
      )}
    >
      <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-3.5">
        <h2 id={titleId} className="text-base font-semibold">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded p-1 text-muted hover:bg-white/5 hover:text-ink"
        >
          <X size={18} aria-hidden />
        </button>
      </div>
      <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
      {footer && <div className="flex justify-end gap-2 border-t border-line px-5 py-3.5">{footer}</div>}
    </dialog>
  );
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  children,
  confirmLabel,
  danger = false,
  busy = false,
  error,
}: {
  open: boolean;
  onClose(): void;
  onConfirm(): void;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  error?: unknown;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant={danger ? "danger" : "primary"} onClick={onConfirm} busy={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-ink-2">
        {children}
        <InlineError error={error} />
      </div>
    </Dialog>
  );
}

/** A labelled form control. */
export function Field({
  label,
  error,
  help,
  children,
}: {
  label: string;
  error?: string;
  help?: string;
  children(props: { id: string; "aria-invalid": boolean; "aria-describedby": string | undefined }): ReactNode;
}) {
  const id = useId();
  const describedBy = error || help ? `${id}-help` : undefined;
  return (
    <div>
      <label htmlFor={id} className="label">
        {label}
      </label>
      {children({ id, "aria-invalid": Boolean(error), "aria-describedby": describedBy })}
      {(error || help) && (
        <p id={describedBy} className={clsx("mt-1 text-xs", error ? "text-ink" : "text-muted")}>
          {error && <span style={{ color: "var(--color-critical)" }}>● </span>}
          {error ?? help}
        </p>
      )}
    </div>
  );
}
